// The run's history as a thread: who did what, when, and one line of what it
// touched in plain words. Event data never carries money (the contract strips
// it), so nothing here needs a financial gate.

import React from 'react';
import EmptyState from '../components/ui/EmptyState';
import { Panel } from '../components/ui/Panel';
import { formatTimestamp } from '../utils/date-utils';
import { DESTINATION_LABELS, type RemovalDestination, type WorkOrderEvent } from './workOrdersApi';

const WORDS: Record<string, string> = {
  suggested: 'suggested this run',
  approved: 'approved it',
  edited: 'changed it',
  released: 'released it to the floor',
  hold: 'put it on hold',
  on_hold: 'put it on hold',
  held: 'put it on hold',
  resume: 'resumed it',
  resumed: 'resumed it',
  cancelled: 'cancelled it',
  closed: 'closed it',
  pick: 'picked a box into Assembly',
  pick_return: 'returned a box from Assembly',
  return: 'returned a box from Assembly',
  box_empty: 'marked a box empty',
  unit_bound: 'bound a drone',
  bound: 'bound a drone',
  unit_completed: 'marked a drone built',
  removal: 'took a part off',
  unit_removal: 'took a part off',
  unit_flagged: 'flagged a drone off the run',
  unit_unflagged: 'cleared a drone\'s flag',
  pick_requested: 'asked the warehouse for a box',
  pick_request_filled: 'filled a request from the line',
};

/** Both directions are written as `pick`; the box going back carries `direction: 'return'`. */
const isReturn = (e: WorkOrderEvent): boolean =>
  e.event !== 'pick' || (e.data || {}).direction === 'return';

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** One line of what the event touched, or null when the data says nothing useful. */
function detail(e: WorkOrderEvent): string | null {
  const d = e.data || {};
  switch (e.event) {
    case 'pick':
    case 'pick_return':
    case 'return': {
      const items = Array.isArray(d.items) ? (d.items as Record<string, unknown>[]) : [];
      const barcode = str(d.barcode);
      const what =
        items.length === 1
          ? [num(items[0].quantity), str(items[0].sku)].filter((x) => x != null).join(' ')
          : items.length > 1
            ? `${items.length} items`
            : null;
      const back = isReturn(e);
      if (!barcode && !what) return null;
      return `${back ? 'Returned' : 'Picked'} ${[barcode, what].filter(Boolean).join(', ')} ${back ? 'from' : 'into'} Assembly`;
    }
    case 'box_empty':
      return [str(d.barcode), str(d.note)].filter(Boolean).join(': ') || null;
    case 'suggested': {
      const want = num(d.line_quantity);
      const balance = num(d.balance);
      if (want == null || balance == null) return null;
      const free = Math.max(0, (num(d.available) ?? 0) - (num(d.committed) ?? 0));
      return `The sale wants ${want}, ${free} finished on the shelf, so ${balance} to build`;
    }
    case 'edited': {
      const parts = Object.entries(d)
        .filter(([, v]) => v && typeof v === 'object' && 'to' in (v as object))
        .map(([k, v]) => {
          const c = v as { from?: unknown; to?: unknown };
          return `${k.replace(/_/g, ' ')} ${String(c.from ?? 'blank')} to ${String(c.to ?? 'blank')}`;
        });
      return parts.length ? parts.join(', ') : null;
    }
    case 'unit_completed': {
      const n = num(d.shortfall_lines);
      if (n) return `Built ${n} ${n === 1 ? 'line' : 'lines'} short`;
      return d.source === 'repair' ? 'Finished in repair' : null;
    }
    case 'pick_requested': {
      const lines = Array.isArray(d.lines) ? (d.lines as Record<string, unknown>[]) : [];
      const named = lines
        .map((l) => [num(l.quantity), str(l.target_name)].filter((x) => x != null).join(' '))
        .filter(Boolean);
      return named.length ? named.join(', ') : null;
    }
    case 'pick_request_filled':
      return [str(d.target_name), str(d.barcode)].filter(Boolean).join(', from ') || null;
    case 'pick_request_cancelled':
      return str(d.reason);
    case 'removal':
    case 'unit_removal': {
      const dest = str(d.destination) as RemovalDestination | null;
      const where = dest && DESTINATION_LABELS[dest] ? `to ${DESTINATION_LABELS[dest]}` : null;
      return [where, str(d.reason)].filter(Boolean).join(': ') || null;
    }
    default:
      return str(d.reason) || str(d.note) || str(d.serial) || str(d.core_hex);
  }
}

function sentence(e: WorkOrderEvent): string {
  const who = e.actor_name || (e.event === 'suggested' ? 'A confirmed sale' : 'Someone');
  const what =
    e.event === 'pick_request_cancelled'
      ? `cancelled the request for ${str((e.data || {}).target_name) || 'a box'}`
      : e.event === 'pick' && isReturn(e)
        ? WORDS.pick_return
        : WORDS[e.event] || e.event.replace(/_/g, ' ');
  const unit = e.unit_position != null ? ` (unit ${e.unit_position})` : '';
  return `${who} ${what}${unit}`;
}

const EventsThread: React.FC<{ events: WorkOrderEvent[] }> = ({ events }) => {
  const ordered = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  return (
    <Panel title="History">
      {ordered.length === 0 ? (
        <EmptyState variant="line">Nothing has happened yet.</EmptyState>
      ) : (
        <ol className="space-y-3">
          {ordered.map((e, i) => {
            const line = detail(e);
            return (
              <li key={String(e.id)} className="relative pl-5">
                <span
                  aria-hidden="true"
                  className={`absolute left-0 top-1.5 h-2 w-2 rounded-full ${
                    i === ordered.length - 1 ? 'bg-primary' : 'bg-surface-container-highest'
                  }`}
                />
                {i < ordered.length - 1 && (
                  <span
                    aria-hidden="true"
                    className="absolute bottom-[-0.75rem] left-[3px] top-4 w-0.5 bg-surface-container-high"
                  />
                )}
                <div className="text-sm text-on-surface">{sentence(e)}</div>
                <div className="text-xs tabular-nums text-on-surface-variant">
                  {formatTimestamp(e.occurred_at, 'MMM D, h:mm A')}
                </div>
                {line && (
                  <p className="mt-1 break-words rounded-md bg-surface-container px-3 py-2 text-sm text-on-surface">
                    {line}
                  </p>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </Panel>
  );
};

export default EventsThread;
